/**
 * Starting a job — the front door of the whole product.
 *
 * Three things, in the order a shop actually has them: who owns the car, what
 * the car is, where it was hit. Nothing else is asked, because everything else
 * can be answered later and asking now is what makes software feel like
 * paperwork.
 *
 * **VIN decode is North America only.** vPIC is free and covers US and Canadian
 * VINs; there is no free equivalent elsewhere. So the decode box only appears
 * where it can work, and manual entry is a first-class path everywhere — not a
 * fallback the estimator is punished with.
 */

import { useState } from 'react';
import { useSession } from '../app/SessionProvider.js';
import { decodeVin } from '../vehicle/vpic.js';
import { normaliseVin, validateVin, vinProblemMessage } from '../vehicle/vin.js';
import type { ImpactPoint } from '../capture/shotlist.js';
import type { CustomerSummary } from '../data/repo.js';

/** vPIC covers vehicles built for these markets. Everywhere else, manual. */
const VIN_DECODE_COUNTRIES = new Set(['US', 'CA']);

const IMPACTS: Array<[ImpactPoint, string]> = [
  ['front_left', 'Front left'], ['front', 'Front'], ['front_right', 'Front right'],
  ['left', 'Left side'], ['all_over', 'All over'], ['right', 'Right side'],
  ['rear_left', 'Rear left'], ['rear', 'Rear'], ['rear_right', 'Rear right'],
  ['roll_over', 'Roll over'], ['non_collision', 'Not a collision'],
];

export interface NewJobDraft {
  customerId: string | null;
  newCustomerName: string;
  newCustomerEmail: string;
  newCustomerNeedsApproval: boolean;
  vin: string;
  year: string;
  make: string;
  model: string;
  trim: string;
  plate: string;
  odometer: string;
  decodeSource: 'manual' | 'vpic';
  impact: ImpactPoint | null;
}

// needsApproval defaults ON. A customer created here who should have paid at
// the counter is one unchecked box away; a customer who needed to approve and
// did not is an estimate the shop finds out was never agreed to.
const EMPTY: NewJobDraft = {
  customerId: null, newCustomerName: '', newCustomerEmail: '', newCustomerNeedsApproval: true,
  vin: '', year: '', make: '', model: '', trim: '', plate: '', odometer: '',
  decodeSource: 'manual', impact: null,
};

export function NewJob({ customers, onCancel, onCreate }: {
  customers: CustomerSummary[];
  onCancel: () => void;
  onCreate: (draft: NewJobDraft) => void;
}) {
  const { t, session } = useSession();
  const canDecode = VIN_DECODE_COUNTRIES.has(session?.country.code ?? '');

  const [d, setD] = useState<NewJobDraft>(EMPTY);
  const [decoding, setDecoding] = useState(false);
  const [vinNote, setVinNote] = useState<{ tone: 'good' | 'warn'; text: string } | null>(null);
  const set = <K extends keyof NewJobDraft>(k: K, v: NewJobDraft[K]) =>
    setD((prev) => ({ ...prev, [k]: v }));

  const decode = async () => {
    const problem = validateVin(d.vin);
    if (problem) {
      setVinNote({ tone: 'warn', text: vinProblemMessage(problem, d.vin) });
      return;
    }
    setDecoding(true);
    setVinNote(null);
    const out = await decodeVin(normaliseVin(d.vin));
    setDecoding(false);

    if (out.kind === 'decoded') {
      const v = out.vehicle;
      setD((prev) => ({
        ...prev,
        year: v.year ? String(v.year) : prev.year,
        make: v.make || prev.make,
        model: v.model || prev.model,
        trim: v.trim || prev.trim,
        decodeSource: 'vpic',
      }));
      setVinNote({
        tone: 'good',
        text: `${[v.year, v.make, v.model, v.trim].filter(Boolean).join(' ')}`
          + ' — check it against the car before you rely on it.',
      });
      return;
    }
    setVinNote({
      tone: 'warn',
      text: out.kind === 'offline'
        ? 'No connection to the decode service. Fill the vehicle in below — it takes '
          + 'twenty seconds and the job is no different.'
        : out.kind === 'not_found'
          ? 'That VIN is not in the NHTSA database. Outside the US and Canada that is '
            + 'normal, not an error. Fill it in below.'
          : `Decode failed (${out.message}). Fill it in below.`,
    });
  };

  const named = d.customerId !== null || d.newCustomerName.trim() !== '';
  // A minimal shape check, not a deliverability check — the same rule the
  // server enforces, so the button disables before the request round-trips
  // rather than after. A new customer with no email is one the approval step
  // will refuse to email later, once the estimate is already issued.
  const emailed = d.customerId !== null || /^[^\s@]+@[^\s@]+$/.test(d.newCustomerEmail.trim());
  const described = d.make.trim() !== '' && d.model.trim() !== '';
  const ready = named && emailed && described && d.impact !== null;

  return (
    <section className="screen">
      <h2>New job</h2>
      <p className="muted lede">
        Who owns it, what it is, where it was hit. Everything else can wait until
        you are standing at the car.
      </p>

      <h3 className="sub">1 · Customer</h3>
      <div className="grid2">
        <label className="field">
          <span>Existing customer</span>
          <select value={d.customerId ?? ''}
                  onChange={(e) => set('customerId', e.target.value || null)}>
            <option value="">— new customer —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{c.needsApproval ? ' (needs approval)' : ''}
              </option>
            ))}
          </select>
        </label>
        {d.customerId === null && (
          <label className="field">
            <span>New customer name</span>
            <input value={d.newCustomerName} placeholder="D. Whitfield"
                   onChange={(e) => set('newCustomerName', e.target.value)} />
          </label>
        )}
      </div>
      {d.customerId === null && (
        <label className="field">
          <span>Email — where the approval goes</span>
          <input type="email" required value={d.newCustomerEmail}
                 placeholder="name@example.com"
                 onChange={(e) => set('newCustomerEmail', e.target.value)} />
        </label>
      )}
      {d.customerId === null && (
        <label className="check">
          <input type="checkbox" checked={d.newCustomerNeedsApproval}
                 onChange={(e) => set('newCustomerNeedsApproval', e.target.checked)} />
          <span>
            This customer approves estimates before work starts
            <em> — fleets and dealers usually do; walk-ins usually do not.</em>
          </span>
        </label>
      )}

      <h3 className="sub">2 · Vehicle</h3>
      <div className="vinrow">
        <label className="field">
          <span>VIN</span>
          <input value={d.vin} maxLength={25} spellCheck={false}
                 placeholder="1FTFW1ET9DFC10312" className="mono"
                 onChange={(e) => { set('vin', e.target.value); setVinNote(null); }} />
        </label>
        {/* Recorded either way — it's the vehicle's own identifier, not just a
            decode key. Only the free lookup is North-America-only; typing one
            in by hand works everywhere and still fills the vehicle record. */}
        {canDecode && (
          <button className="btn" disabled={decoding || d.vin.trim() === ''}
                  onClick={() => void decode()}>
            {decoding ? 'Decoding…' : 'Decode'}
          </button>
        )}
      </div>
      {canDecode ? (
        vinNote && (
          <div className={`note ${vinNote.tone}`}>
            <b>{vinNote.tone === 'good' ? 'Decoded' : 'Not decoded'}</b>
            {vinNote.text}
          </div>
        )
      ) : (
        <div className="note">
          <b>Year, make, model and trim are by hand here</b>
          Free VIN decoding exists for US and Canadian vehicles only. There is no
          public equivalent for {session?.country.name} — so the VIN above is kept
          on the record, but the four fields below are the normal path.
        </div>
      )}

      <div className="grid4">
        <label className="field"><span>Year</span>
          <input value={d.year} inputMode="numeric" maxLength={4}
                 onChange={(e) => set('year', e.target.value)} /></label>
        <label className="field"><span>Make</span>
          <input value={d.make} onChange={(e) => set('make', e.target.value)} /></label>
        <label className="field"><span>Model</span>
          <input value={d.model} onChange={(e) => set('model', e.target.value)} /></label>
        <label className="field"><span>Trim</span>
          <input value={d.trim} onChange={(e) => set('trim', e.target.value)} /></label>
      </div>
      <div className="grid2">
        <label className="field"><span>Plate</span>
          <input value={d.plate} className="mono"
                 onChange={(e) => set('plate', e.target.value)} /></label>
        <label className="field">
          <span>Odometer ({session?.country.distanceUnit})</span>
          <input value={d.odometer} inputMode="numeric"
                 onChange={(e) => set('odometer', e.target.value)} /></label>
      </div>

      <h3 className="sub">3 · Where was it hit?</h3>
      <p className="muted small">
        This chooses the shot list. Get it wrong and the app asks for the wrong
        photographs — you can change it later.
      </p>
      <div className="impacts">
        {IMPACTS.map(([id, label]) => (
          <button key={id} type="button"
                  className={`impact ${d.impact === id ? 'on' : ''}`}
                  aria-pressed={d.impact === id}
                  onClick={() => set('impact', id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="btnrow">
        <button className="btn primary" disabled={!ready} onClick={() => onCreate(d)}>
          Start this job
        </button>
        <button className="btn" onClick={onCancel}>{t('Cancel')}</button>
      </div>
      {!ready && (
        <p className="muted small">
          Still needed:{' '}
          {[!named && 'a customer', !described && 'a make and model',
            !d.impact && 'where it was hit'].filter(Boolean).join(' · ')}
        </p>
      )}
    </section>
  );
}
